import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useCart } from "../../contexts/CartProvider";
import { useAuth } from "../../contexts/AuthProvider";
import { http } from "../../services/http";
import { useTranslation } from "react-i18next";
import {
  ShoppingBag,
  Menu as MenuIcon,
  LogIn,
  LogOut,
  User,
  FileText,
  ChevronDown,
  Star,
  Sparkles,
  Globe,
  X,
  ChevronRight,
} from "lucide-react";

function cn(...x: Array<string | false | undefined | null>) {
  return x.filter(Boolean).join(" ");
}

function useIsScrolled(threshold = 8) {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > threshold);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [threshold]);

  return scrolled;
}

function useClickOutside(
  refs: Array<React.RefObject<HTMLElement | null>>,
  onOutside: () => void,
) {
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      const inside = refs.some((ref) => ref.current?.contains(target));
      if (!inside) onOutside();
    };

    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [refs, onOutside]);
}

type ProfileApi = {
  availablePoints?: number;
  pointValue?: number;
  pointToMoneyRate?: number;
  user?: {
    username?: string;
    email?: string;
    pointAccumulated?: number;
    pointUsed?: number;
  };
};

const PROFILE_ENDPOINT = "/api/profile";

function NavPill(props: { active: boolean; children: React.ReactNode }) {
  return (
    <span
      className={cn(
        "relative inline-flex h-11 items-center rounded-full px-5 text-[11px] font-medium uppercase tracking-[0.18em] transition duration-300",
        props.active
          ? "text-brand-ivory"
          : "text-brand-brown/68 hover:text-brand-brown",
      )}
    >
      <span
        className={cn(
          "absolute inset-0 rounded-full border transition duration-300",
          props.active
            ? "border-brand-brown bg-brand-brown shadow-[0_14px_30px_rgba(43,31,26,0.16)]"
            : "border-brand-brown/8 bg-white/55 hover:border-brand-brown/18 hover:bg-white",
        )}
      />
      <span className="relative">{props.children}</span>
    </span>
  );
}

function ActionPill(props: {
  children: React.ReactNode;
  onClick?: () => void;
  className?: string;
  as?: "button" | "div";
}) {
  const Component = props.as ?? "button";

  return (
    <Component
      {...(Component === "button"
        ? { type: "button", onClick: props.onClick }
        : {})}
      className={cn(
        "inline-flex h-11 items-center gap-2 rounded-full border border-brand-brown/10 bg-white/72 px-4",
        "text-[11px] font-medium uppercase tracking-[0.16em] text-brand-brown/78",
        "transition duration-300 hover:border-brand-brown/22 hover:bg-white hover:text-brand-brown",
        props.className,
      )}
    >
      {props.children}
    </Component>
  );
}

export default function Header() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const { toggle: toggleCart, count, isOpen: isCartOpen } = useCart();
  const { user, logout } = useAuth();

  const [menuOpen, setMenuOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const [profile, setProfile] = useState<ProfileApi | null>(null);
  const [profileLoading, setProfileLoading] = useState(false);

  const accountWrapRef = useRef<HTMLDivElement>(null);

  const scrolled = useIsScrolled(10);
  const lang = i18n.language?.startsWith("en") ? "en" : "vi";
  const moneyLocale = lang === "en" ? "en-US" : "vi-VN";

  const closeMenu = () => setMenuOpen(false);
  const closeAccount = () => setAccountOpen(false);

  useClickOutside([accountWrapRef], closeAccount);

  const toggleLanguage = () => {
    i18n.changeLanguage(lang === "vi" ? "en" : "vi");
  };

  const handleLogout = () => {
    if (isCartOpen) toggleCart();
    logout();
    closeAccount();
    closeMenu();
    navigate("/auth", { replace: true });
  };

  const handleOpenCart = () => {
    if (menuOpen) closeMenu();
    closeAccount();
    toggleCart();
  };

  useEffect(() => {
    closeMenu();
    closeAccount();
  }, [location.pathname]);

  useEffect(() => {
    if (!menuOpen) {
      document.body.classList.remove("modal-open");
      return;
    }

    document.body.classList.add("modal-open");
    return () => document.body.classList.remove("modal-open");
  }, [menuOpen]);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        closeMenu();
        closeAccount();
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  useEffect(() => {
    let mounted = true;

    const run = async () => {
      if (!user) {
        setProfile(null);
        return;
      }

      setProfileLoading(true);
      try {
        const res = await http.get(PROFILE_ENDPOINT);
        if (!mounted) return;
        setProfile(res.data);
      } catch {
        if (!mounted) return;
        setProfile(null);
      } finally {
        if (mounted) setProfileLoading(false);
      }
    };

    run();
    return () => {
      mounted = false;
    };
  }, [user]);

  const navItems = useMemo(
    () => [
      { to: "/collection", labelKey: "nav.shop" },
      { to: "/drops", labelKey: "nav.drops" },
      { to: "/voucher-game", labelKey: "nav.voucherGame" },
      { to: "/policies", labelKey: "nav.policies" },
    ],
    [],
  );

  const points =
    profile?.availablePoints ??
    profile?.user?.pointAccumulated ??
    (user as any)?.availablePoints ??
    (user as any)?.points ??
    null;

  const pointToMoneyRate = profile?.pointToMoneyRate ?? 1000;
  const pointValue =
    profile?.pointValue ?? (points != null ? points * pointToMoneyRate : null);

  return (
    <>
      <header className="fixed inset-x-0 top-0 z-50">
        <div
          className={cn(
            "absolute inset-0 transition-all duration-300",
            scrolled
              ? "bg-[#f6efe4]/92 backdrop-blur-xl shadow-[0_14px_40px_rgba(43,31,26,0.08)]"
              : "bg-[#f6efe4]/78 backdrop-blur-lg",
          )}
        />
        <div className="absolute inset-x-0 bottom-0 h-px bg-brand-brown/10" />

        <div className="relative mx-auto flex h-[74px] max-w-7xl items-center justify-between px-4 text-brand-brown sm:h-[80px] sm:px-6 lg:px-10">
          <Link
            to="/"
            className="group inline-flex min-w-0 items-center gap-3 rounded-full pr-2 transition"
            aria-label="OWL DREAM"
          >
            <div className="flex h-11 w-11 items-center justify-center overflow-hidden rounded-full border border-brand-brown/10 bg-white/72 shadow-[0_8px_24px_rgba(43,31,26,0.05)] sm:h-12 sm:w-12">
              <img
                src={`${process.env.PUBLIC_URL}/logo/owl-icon.png`}
                alt="OWL DREAM"
                className="h-7 w-auto object-contain sm:h-8"
              />
            </div>

            <div className="min-w-0">
              <p className="truncate font-brand-logo text-[12px] uppercase tracking-[0.22em] text-brand-brown sm:text-[13px]">
                OWL DREAM
              </p>
              <p className="hidden text-[10px] uppercase tracking-[0.18em] text-brand-brown/42 sm:block">
                Streetwear / Art Identity
              </p>
            </div>
          </Link>

          <nav className="hidden items-center gap-2 rounded-full border border-brand-brown/8 bg-white/52 p-1.5 shadow-[0_10px_30px_rgba(43,31,26,0.04)] lg:flex">
            {navItems.map((item) => (
              <NavLink key={item.to} to={item.to} end={item.to === "/"}>
                {({ isActive }) => (
                  <NavPill active={isActive}>{t(item.labelKey)}</NavPill>
                )}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-2 sm:gap-3">
            <ActionPill
              onClick={toggleLanguage}
              className="hidden md:inline-flex"
            >
              <Globe className="h-4 w-4" />
              {lang === "vi" ? "EN" : "VI"}
            </ActionPill>

            {user ? (
              <div ref={accountWrapRef} className="relative hidden md:block">
                <button
                  onClick={() => setAccountOpen((v) => !v)}
                  className="inline-flex h-11 items-center gap-2 rounded-full border border-brand-brown/10 bg-white/72 px-4 text-[11px] font-medium uppercase tracking-[0.16em] text-brand-brown/78 transition duration-300 hover:border-brand-brown/22 hover:bg-white hover:text-brand-brown"
                  aria-haspopup="menu"
                  aria-expanded={accountOpen}
                  type="button"
                >
                  <User className="h-4 w-4" />
                  <span className="hidden xl:inline">{t("header.myAccount")}</span>
                  <ChevronDown
                    className={cn(
                      "h-4 w-4 transition-transform duration-300",
                      accountOpen && "rotate-180",
                    )}
                  />
                </button>

                {accountOpen && (
                  <div
                    className="absolute right-0 top-[calc(100%+12px)] z-[70] w-[320px] overflow-hidden rounded-[28px] border border-brand-brown/10 bg-[#fbf7f0]/96 text-brand-brown shadow-[0_30px_100px_rgba(43,31,26,0.16)] backdrop-blur-xl"
                    role="menu"
                    aria-label={t("header.accountMenuAria")}
                  >
                    <div className="border-b border-brand-brown/8 bg-white/65 px-5 py-4">
                      <p className="truncate text-[13px] font-medium tracking-[0.01em] text-brand-brown">
                        {(profile?.user?.username as any) ||
                          (user as any)?.username ||
                          t("header.user")}
                      </p>
                      <p className="mt-1 truncate text-[11px] uppercase tracking-[0.14em] text-brand-brown/48">
                        {(profile?.user?.email as any) ||
                          (user as any)?.email ||
                          "—"}
                      </p>
                    </div>

                    <div className="space-y-4 px-5 py-4">
                      <div className="rounded-[22px] border border-brand-brown/8 bg-white/70 p-4">
                        <div className="flex items-center justify-between gap-3">
                          <div className="flex items-center gap-2 text-brand-brown/72">
                            <Star className="h-4 w-4 text-brand-gold" />
                            <span className="text-[11px] uppercase tracking-[0.14em]">
                              {t("header.availablePoints")}
                            </span>
                          </div>
                          <span className="text-base font-medium text-brand-brown">
                            {profileLoading ? "…" : points ?? "—"}
                          </span>
                        </div>

                        <div className="mt-2 text-[11px] leading-5 text-brand-brown/52">
                          {points != null ? (
                            <>
                              ~ {Number(pointValue ?? 0).toLocaleString(moneyLocale)}₫ •{" "}
                              {t("header.pointRule", {
                                rate: Number(pointToMoneyRate).toLocaleString(
                                  moneyLocale,
                                ),
                              })}
                            </>
                          ) : (
                            "—"
                          )}
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Link
                          to="/profile"
                          onClick={closeAccount}
                          className="flex h-12 items-center justify-between rounded-[18px] border border-brand-brown/8 bg-white/55 px-4 text-[11px] font-medium uppercase tracking-[0.14em] text-brand-brown/88 transition hover:border-brand-brown/16 hover:bg-white"
                          role="menuitem"
                        >
                          <span className="flex items-center gap-3">
                            <User className="h-4 w-4" />
                            {t("header.myProfile")}
                          </span>
                          <ChevronRight className="h-4 w-4 text-brand-brown/40" />
                        </Link>

                        <Link
                          to="/voucher-game"
                          onClick={closeAccount}
                          className="flex h-12 items-center justify-between rounded-[18px] border border-brand-brown/8 bg-white/55 px-4 text-[11px] font-medium uppercase tracking-[0.14em] text-brand-brown/88 transition hover:border-brand-brown/16 hover:bg-white"
                          role="menuitem"
                        >
                          <span className="flex items-center gap-3">
                            <Sparkles className="h-4 w-4 text-brand-gold" />
                            {t("nav.voucherGame")}
                          </span>
                          <ChevronRight className="h-4 w-4 text-brand-brown/40" />
                        </Link>

                        <button
                          onClick={handleLogout}
                          className="flex h-12 w-full items-center justify-between rounded-[18px] border border-brand-brown/8 bg-white/55 px-4 text-[11px] font-medium uppercase tracking-[0.14em] text-brand-brown/88 transition hover:border-brand-brown/16 hover:bg-white"
                          role="menuitem"
                          type="button"
                        >
                          <span className="flex items-center gap-3">
                            <LogOut className="h-4 w-4" />
                            {t("header.logout")}
                          </span>
                          <ChevronRight className="h-4 w-4 text-brand-brown/40" />
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <Link to="/auth" className="hidden md:inline-flex">
                <ActionPill as="div">
                  <LogIn className="h-4 w-4" />
                  <span>{t("header.login")}</span>
                </ActionPill>
              </Link>
            )}

            <button
              onClick={handleOpenCart}
              className="relative inline-flex h-11 items-center gap-2 rounded-full border border-brand-brown/10 bg-white/72 px-4 text-[11px] font-medium uppercase tracking-[0.16em] text-brand-brown/82 transition duration-300 hover:border-brand-brown/22 hover:bg-white hover:text-brand-brown"
              type="button"
              aria-label={t("header.cart")}
            >
              <ShoppingBag className="h-4 w-4" />
              <span className="hidden sm:inline">{t("header.cart")}</span>
              <span className="inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-brand-gold px-1.5 text-[10px] font-semibold text-brand-brown">
                {count}
              </span>
            </button>

            <button
              onClick={() => setMenuOpen(true)}
              className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-brand-brown/10 bg-white/72 text-brand-brown transition duration-300 hover:border-brand-brown/22 hover:bg-white lg:hidden"
              aria-label={t("header.openMenuAria")}
              type="button"
            >
              <MenuIcon className="h-5 w-5" />
            </button>
          </div>
        </div>
      </header>

      <div
        className={cn(
          "fixed inset-0 z-[60] transition duration-300 lg:hidden",
          menuOpen ? "pointer-events-auto" : "pointer-events-none",
        )}
      >
        <div
          className={cn(
            "absolute inset-0 bg-black/36 transition-opacity duration-300",
            menuOpen ? "opacity-100" : "opacity-0",
          )}
          onClick={closeMenu}
        />

        <div
          className={cn(
            "absolute inset-y-0 right-0 flex w-[88%] max-w-sm flex-col bg-[#f7f1e7] text-brand-brown shadow-[0_20px_80px_rgba(43,31,26,0.18)] transition-transform duration-300",
            menuOpen ? "translate-x-0" : "translate-x-full",
          )}
        >
          <div className="border-b border-brand-brown/8 px-5 pb-5 pt-6">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-[11px] uppercase tracking-[0.18em] text-brand-brown/42">
                  OWL DREAM
                </p>
                <h2 className="mt-3 text-[24px] font-medium leading-[1.02] text-brand-brown sm:text-[26px]">
                  Clean navigation. Stronger brand feel.
                </h2>
              </div>

              <button
                onClick={closeMenu}
                className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-brand-brown/10 bg-white/70 text-brand-brown transition hover:border-brand-brown/18 hover:bg-white"
                type="button"
                aria-label="Close menu"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="mt-6 flex items-center gap-3">
              <button
                onClick={toggleLanguage}
                className="inline-flex h-11 items-center gap-2 rounded-full border border-brand-brown/10 bg-white/72 px-4 text-[11px] font-medium uppercase tracking-[0.16em] text-brand-brown/82"
                type="button"
              >
                <Globe className="h-4 w-4" />
                {lang === "vi" ? "EN" : "VI"}
              </button>

              <button
                onClick={handleOpenCart}
                className="inline-flex h-11 items-center gap-2 rounded-full border border-brand-brown/10 bg-white/72 px-4 text-[11px] font-medium uppercase tracking-[0.16em] text-brand-brown/82"
                type="button"
              >
                <ShoppingBag className="h-4 w-4" />
                {t("header.cart")}
                <span className="inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-brand-gold px-1.5 text-[10px] font-semibold text-brand-brown">
                  {count}
                </span>
              </button>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto px-5 py-6">
            <div className="space-y-2">
              {navItems.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  className={({ isActive }) =>
                    cn(
                      "flex h-14 items-center justify-between rounded-[20px] border px-4 text-[11px] font-medium uppercase tracking-[0.16em] transition",
                      isActive
                        ? "border-brand-brown bg-brand-brown text-brand-ivory shadow-[0_14px_30px_rgba(43,31,26,0.12)]"
                        : "border-brand-brown/10 bg-white/62 text-brand-brown/76 hover:border-brand-brown/18 hover:bg-white hover:text-brand-brown",
                    )
                  }
                >
                  <span>{t(item.labelKey)}</span>
                  {item.to === "/policies" ? (
                    <FileText className="h-4 w-4" />
                  ) : item.to === "/voucher-game" ? (
                    <Sparkles className="h-4 w-4 text-brand-gold" />
                  ) : (
                    <ChevronRight className="h-4 w-4" />
                  )}
                </NavLink>
              ))}
            </div>

            <div className="my-6 h-px bg-brand-brown/8" />

            {user ? (
              <div className="space-y-2">
                <Link
                  to="/profile"
                  className="flex h-14 items-center gap-3 rounded-[20px] border border-brand-brown/10 bg-white/62 px-4 text-[11px] font-medium uppercase tracking-[0.16em] text-brand-brown/80 transition hover:border-brand-brown/18 hover:bg-white hover:text-brand-brown"
                >
                  <User className="h-4 w-4" />
                  {t("header.myAccount")}
                </Link>

                <button
                  onClick={handleLogout}
                  className="flex h-14 w-full items-center gap-3 rounded-[20px] border border-brand-brown/10 bg-white/62 px-4 text-[11px] font-medium uppercase tracking-[0.16em] text-brand-brown/80 transition hover:border-brand-brown/18 hover:bg-white hover:text-brand-brown"
                  type="button"
                >
                  <LogOut className="h-4 w-4" />
                  {t("header.logout")}
                </button>
              </div>
            ) : (
              <Link
                to="/auth"
                className="flex h-14 items-center gap-3 rounded-[20px] border border-brand-brown/10 bg-white/62 px-4 text-[11px] font-medium uppercase tracking-[0.16em] text-brand-brown/80 transition hover:border-brand-brown/18 hover:bg-white hover:text-brand-brown"
              >
                <LogIn className="h-4 w-4" />
                {t("header.login")}
              </Link>
            )}
          </div>

          <div className="border-t border-brand-brown/8 px-5 py-5">
            <div className="rounded-[22px] border border-brand-brown/10 bg-white/62 p-4">
              <p className="text-[10px] uppercase tracking-[0.18em] text-brand-brown/42">
                Current mood
              </p>
              <p className="mt-2 text-sm leading-6 text-brand-brown/72">
                Minimal / clean / readable / product-first.
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
